// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';
import omit from 'lodash/omit';
import isEqual from 'lodash/isEqual';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {filterAttributes} from 'common/util';
import {colors} from 'common/util/colors';
import {KEY_VALUES, propagateEvent} from 'common/util/dom';
import {getModifierKeys, ModifierKeys} from 'common/util/keyboard';
import {lexicalCompose} from 'common/util/function';
import {fuzzyMatch} from 'common/util/fuzzy-match';
import {trackInitialization} from 'common/util/component-helpers';
import {debounce} from 'common/util/lodash-wrapped';
import {IronContextRegistry} from 'common/report/context-registry';
import {Directions} from 'common/components/drop-menu/types';
import type {Translatable} from 'common/i18n';
import {LocalizationStore} from 'common/i18n/store';
import {i18next} from 'common/i18n';
import {SimpleNavManager} from 'common/contexts/nav-manager/simple-nav-manager';

import css from './index.inline.styl';
// eslint-disable-next-line import/no-cycle
import {template} from './template';
import {
  CustomSearchMatcherFn,
  ElementRefs,
  ExpanderItemClickEvent,
  ExpansionToggleItemClickEvent,
  ExpansionToggleLabelMode,
  ItemClickEvent,
  ItemFocusEvent,
  ItemsMenuAttrs,
  ItemsMenuJsxProps,
  ItemsMenuParams,
  ItemsMenuSize,
  ItemsMenuState,
  ItemsMenuTheme,
  Section,
  SectionItemContextPanelTransform,
  SectionsFromSearchInputFn,
  SelectAllMechanism,
  SelectedTheme,
  WrappedSection,
  WrappedSectionDetails,
  WrappedSectionItem,
} from './types';
import {
  DESELECT_ALL_ITEM,
  EXPANSION_TOGGLE_ITEM,
  MAX_CSV_MATCHES,
  PROGRESSIVE_SIZE,
  SELECT_ALL_ITEM,
  SELECT_ALL_MATCHING_ITEM,
  SelectionState,
  SHOW_ALL_ITEM,
  SHOW_HIDDEN_SECTION_ITEM,
  SUB_MENU_HOVER_DELAY_MS,
} from './constants';
import {
  addIndicesToWrappedItems,
  countTotalItems,
  createItemsKeyMap,
  findSelectedWrappedItems,
  getVisibleSections,
  itemHasSubSections,
  sortOnProperty,
  unwrapSections,
  wrapSectionItem,
  wrapSections,
} from './utils';

export class ItemsMenu extends Component<
  ItemsMenuState,
  ItemsMenuAttrs,
  unknown,
  unknown,
  IronContextRegistry,
  ItemsMenuParams
> {
  private _keydownProxyEl = null;
  private showScrollShadow = false;
  private elRefs: ElementRefs = {};
  private isScrollingIntoView = false;
  declare contextPanelPositionParentElement: HTMLElement;

  private declare _contextPanelTransform: SectionItemContextPanelTransform;
  static get attrsSchema(): AttrsSchema<ItemsMenuAttrs> {
    return {
      ...super.attrsSchema,
      'context-panel-alignment': {
        type: `string`,
        enum: Object.values(Directions),
        default: Directions.Right,
        description: `which side of the items menu should the context panel be placed`,
      },
      'disable-search-sort': {
        type: `boolean`,
        description: `Prevents search results from being sorted by this component.`,
      },
      'disable-search-filter-matching-parts': {
        type: `boolean`,
        description: `Disables the splitting and styling of an item.label's matching substrings to the provided search-filter.`,
      },
      'disable-first-item-padding': {
        type: `boolean`,
        description: `Disables padding on the first item of the first section.
          Why does this exist? Items Menu has historically provided a magical top padding to make implementations look nicer.
          This padding does not provide the flexibility when a caller is managing their own header.
          Ideally, we never provide a top padding and the caller in responsible for all styling.
        `,
      },
      'empty-message': `string`,
      'empty-search-results-message': {
        type: `string`,
        default: `Your search returned no results`,
        description: `Message to show when a filter results in an empty list`,
      },
      /** @deprecated use "selected-theme" instead */
      multiple: {type: `boolean`, description: `set "true" to enable multi-selection mode`},
      'project-is-free-plan': `boolean`,
      'search-exact': {type: `boolean`, description: `only match search-filter substrings, don't fuzzy-match`},
      'search-filter': `string`,
      sections: {type: `json`, default: [], tsType: `Section[]`},
      'sections-hint': `string`,
      'select-all-mechanism': {
        type: `string`,
        enum: Object.values(SelectAllMechanism),
        default: SelectAllMechanism.Full,
      },
      'selected-theme': {
        type: `string`,
        enum: Object.values(SelectedTheme),
        default: SelectedTheme.Default,
        description: `whether it's single/multi-selection and the styling to use for displaying selected items`,
      },
      'show-csv-matches': `boolean`,
      'show-section-label-matches': `boolean`,
      'show-select-all': `boolean`,
      'show-select-all-matching': `boolean`,
      'select-all-disabled': `boolean`,
      'select-all-matching-disabled': `boolean`,
      'select-all-tooltip': `json`,
      size: {
        type: `string`,
        enum: Object.values(ItemsMenuSize),
        default: ItemsMenuSize.Default,
      },
      'disable-auto-focus': `boolean`,
      'align-all-text': {
        type: `boolean`,
        description: `align text whether icons are present or not. use when you have icons sparsely spread through the menu`,
      },
      theme: {
        type: `string`,
        enum: Object.values(ItemsMenuTheme),
        default: ItemsMenuTheme.Default,
      },
    };
  }

  get config() {
    return {
      useShadowDom: true,
      css,
      template,
      defaultState: {
        customSearchMatcherFn: null,
        fullyShownSections: {},
        isPillFocused: false,
        openedSubMenuIndex: null,
        keyedItems: {},
        progressiveListSize: PROGRESSIVE_SIZE,
        sectionsFromInputFn: null,
        sections: [],
        selectionState: SelectionState.None,
        tooltipData: null,

        // Focused
        focusedItemIndex: -1,
        focusedItem: {sectionDetails: null, wrappedItem: null},
      } as ItemsMenuState,
      params: {
        onHoverItem: Function,
        onHoverOut: Function,
      },
      contexts: [`i18n` as const, `navManager` as const],
      defaultContexts: {
        i18n: new LocalizationStore(i18next),
        navManager: new SimpleNavManager(),
      },
      helpers: {
        propagateEvent: (ev) => propagateEvent(this, ev),
        filterAttributes,
        getDisplaySections: () => this.getDisplaySections(),
        getElRef: (ref: string) => this.elRefs[ref],
        setElRef: (ref: string, el: HTMLElement) => {
          this.elRefs[ref] = el;
          this.update({});
        },

        handleSubItemsClickItem: (ev) => this.handleSubItemsClickItem(ev),
        handleSubItemsFocusItem: (ev) => {
          this.update({hasInteractedWithSubMenu: true});
          propagateEvent(this, ev);
        },
        handleSubMenuMouseEnter: () => {
          this.update({hasInteractedWithSubMenu: true});
          this.debouncedUnFocusItem.cancel();
        },
        handleSubItemsNavigateBack: () => this.closeSubMenu(),
        handleScroll: (ev) => this.handleScroll(ev),
        handlePostPatch: () => {
          // When this component is patched, the height of it could change. Send a draw event so users of the
          // component can respond to the height change accordingly.
          this.dispatchCustomEvent(`draw`);
        },
        handleClickItem: (item, ev) => this.handleClickItem(item, ev),
        handleMouseEnterItem: (item: WrappedSectionItem) => {
          if (this.isScrollingIntoView) {
            return;
          }
          this.params.onHoverItem?.(item.wrapped);
          this.update({isPillFocused: false});
          this.focusItem(item.index, {scrollIntoView: false, canShowSubItems: true});
        },
        handleMouseLeave: () => {
          if (
            this.state.tooltipData ||
            this.isScrollingIntoView ||
            /** when there's an active sub-menu, we wish to preserve it open when it has been interacted with */
            (this.state.openedSubMenuIndex !== null && this.state.hasInteractedWithSubMenu)
          ) {
            return;
          }
          this.params.onHoverOut?.();

          this.debouncedUnFocusItem();
        },
        handleMouseEnterPill: () => this.update({isPillFocused: true}),
        handleMouseLeavePill: () => this.update({isPillFocused: false}),
        handleClickPill: (item, ev) => {
          // Pill click automatically bubbles up and becomes item click. We don't want that
          if (ev) {
            ev.stopPropagation();
          }

          this.handleClickPill(item, getModifierKeys(ev));
        },
        hasSlot: (slotName: string): boolean => {
          return !!this.querySelector(`[slot="${slotName}"]`);
        },
      },
    };
  }

  constructor() {
    super();
    // Since handlers can be called on proxy elements, use bind for consistent `this`
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    trackInitialization(this);
    this.addEventListener(`keydown`, this.handleKeydown, true);
    window.addEventListener(`resize`, this.handleResize);
  }

  disconnectedCallback() {
    if (!this.initialized) {
      return;
    }

    super.disconnectedCallback();
    window.removeEventListener(`resize`, this.handleResize);
    this.removeEventListener(`keydown`, this.handleKeydown, true);
    this.keydownProxyEl = null;
  }

  handleInsertScreenListContainer(elm) {
    this.helpers.setElRef(`containerEl`, elm);
  }

  dispatchCustomEvent(name, opts = {}) {
    this.dispatchEvent(new CustomEvent(name, opts));
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    switch (attr) {
      case `sections`:
        this.sections = this.attr(`sections`);
        break;
      case `search-filter`: {
        const searchCleared = oldVal !== `` && newVal === ``;
        const searchStarted = newVal !== `` && oldVal === ``;
        if (searchCleared) {
          this.update({fullyShownSections: {}});
          this.focusItem(0);
        } else if (searchStarted) {
          this.focusItem(0);
        } else {
          this.focusItem(this.state.focusedItemIndex);
        }
        break;
      }
    }
  }

  /**
   *  Handles arrow and tab keydown events from domEl for keyboard accessibility
   */
  set keydownProxyEl(domEl: HTMLElement) {
    if (this._keydownProxyEl) {
      this._keydownProxyEl.removeEventListener(`keydown`, this.handleKeydown, false);
    }
    this._keydownProxyEl = domEl;
    if (this._keydownProxyEl) {
      this._keydownProxyEl.addEventListener(`keydown`, this.handleKeydown, false);
    }
  }

  get keydownProxyEl() {
    return this._keydownProxyEl;
  }

  /**
   * Function that returns a new section to be added depending on user input
   */
  set sectionsFromInputFn(sectionsFromInputFn: SectionsFromSearchInputFn) {
    this.update({sectionsFromInputFn});
  }

  /*
   * ItemsMenu should not be in charge of rendering the ContextPanel.
   * Ideally, we create a widget that joins items-menu and context panel for self managed context and rendering efficiency.
   */
  /** @param value - function that returns context panel data to be shown for focused item */
  set contextPanelTransform(contextPanelTransform: SectionItemContextPanelTransform) {
    this._contextPanelTransform = contextPanelTransform;
    if (contextPanelTransform === null || this.state.tooltipData === null) {
      // GOOT Patch: ItemsMenu is used as the defining width for the classic query-block menus.
      // This means that for a momentary render the parent container has no calculated width.
      // The context panel will be stacked in an incorrect location if it is rendered in the same frame as the ItemsMenu's insert render
      // This will not be an issue when we pull context menu out of the ItemsMenu. For now, safely wait a frame to ensure items menu has existed.
      requestAnimationFrame(() => this.showContextPanelIfNeeded());
    }
  }

  get contextPanelTransform() {
    return this._contextPanelTransform;
  }

  set customSearchMatcherFn(customSearchMatcherFn: CustomSearchMatcherFn) {
    this.update({customSearchMatcherFn});
  }

  /**
   *  sections passed as a prop for perf purposes when passing large number of items.
   *  JSON serialization/deserialization can have a noticeable perf impact.
   */
  set sections(sections: Section[]) {
    if (isEqual(sections, this.state.sections)) {
      return;
    }
    const isInitializingSections = !this.state.sections?.length && Boolean(sections?.length);

    const {fullyShownSections} = this.state;
    sections.forEach((section, sectionIndex) => {
      const {expansionToggle} = section;
      if (!expansionToggle) {
        return;
      }
      if (`isExpanded` in expansionToggle) {
        fullyShownSections[sectionIndex] = expansionToggle.isExpanded;
      } else if (isInitializingSections) {
        fullyShownSections[sectionIndex] = expansionToggle.initialIsExpanded;
      }
    });

    this.update({
      sections,
      selectionState: this.getSelectionState(sections),
      keyedItems: createItemsKeyMap(sections),
    });

    if (this.attr(`disable-auto-focus`) === false) {
      if (isInitializingSections) {
        const focusedItemIndex = this.findSelectedItem()?.index ?? 0;
        this.focusItem(focusedItemIndex, {scrollIntoView: focusedItemIndex !== 0, isAutoFocus: true});
      } else {
        this.focusItem(this.state.focusedItemIndex, {isAutoFocus: true});
      }
    }
  }

  debouncedCloseSubMenu = debounce(() => {
    this.update({
      openedSubMenuIndex: null,
      hasInteractedWithSubMenu: false,
    });
  }, SUB_MENU_HOVER_DELAY_MS);

  isSubItemsOpen(itemIndex) {
    return this.state.openedSubMenuIndex === itemIndex;
  }

  debouncedOpenSubMenu = debounce((itemIndex) => {
    this.update({openedSubMenuIndex: itemIndex});
  }, SUB_MENU_HOVER_DELAY_MS);

  debouncedUnFocusItem = debounce(() => {
    this.closeSubMenu({delay: false});
    this.update({
      focusedItemIndex: -1,
      focusedItem: {sectionDetails: null, wrappedItem: null},
      tooltipData: null,
    });
  }, SUB_MENU_HOVER_DELAY_MS);

  closeSubMenu({delay = true}: {delay?: boolean} = {}) {
    if (delay) {
      this.debouncedCloseSubMenu();
    } else {
      this.update({
        openedSubMenuIndex: null,
        hasInteractedWithSubMenu: false,
      });
    }
  }

  openSubMenu(itemIndex, {delay = true}: {delay?: boolean} = {}) {
    this.debouncedCloseSubMenu.cancel();

    if (delay) {
      this.debouncedOpenSubMenu(itemIndex);
    } else {
      this.update({openedSubMenuIndex: itemIndex});
    }
  }

  handleKeydown(ev) {
    const {isPillFocused, openedSubMenuIndex} = this.state;
    const preventAction = () => {
      ev.stopPropagation();
      ev.preventDefault();
    };

    /** when the list has an active sub list, delegate keyboard actions to the nested mp-items-menu */
    if (openedSubMenuIndex !== null) {
      return;
    }

    switch (ev.code) {
      case KEY_VALUES.ARROW_UP:
        this.focusItem(Math.max(this.state.focusedItemIndex - 1, 0));
        preventAction();
        break;
      case KEY_VALUES.ARROW_DOWN:
        this.focusItem(Math.min(this.state.focusedItemIndex + 1, countTotalItems(this.getFilteredSections()) - 1));
        preventAction();
        break;
      case KEY_VALUES.ARROW_LEFT: {
        this.closeSubMenu();
        this.dispatchCustomEvent(`navigateBack`);
        break;
      }
      case KEY_VALUES.ARROW_RIGHT: {
        const {wrappedItem} = this.state.focusedItem;
        if (itemHasSubSections(wrappedItem.wrapped)) {
          this.openSubMenu(wrappedItem.index, {delay: false});
          preventAction();
        }
        break;
      }
      case KEY_VALUES.TAB: {
        // We should never be swallowing tab presses since they are used by browser to switch focus
        // however to preserve backwards compatibility, we only swallow tab presses if item has pill
        const {wrappedItem} = this.state.focusedItem;
        if (wrappedItem && wrappedItem.wrapped.hasPill) {
          this.update({isPillFocused: !isPillFocused});
          preventAction();
        }
        break;
      }
      case KEY_VALUES.ENTER: {
        // user can press enter even though are no items or search results are empty
        const {wrappedItem} = this.state.focusedItem;
        if (wrappedItem && !wrappedItem.wrapped.isDisabled && !wrappedItem.wrapped.isReadOnly) {
          if (isPillFocused) {
            this.handleClickPill(wrappedItem, getModifierKeys(ev));
          } else {
            this.handleClickItem(wrappedItem, ev);

            // some menu items are links, make sure we properly navigate
            // because this component doesn't use native browser focus.
            if (wrappedItem.wrapped.href) {
              this.getFocusedItemAnchorEl()?.click();
            }
          }
          preventAction();
        }
        break;
      }
      default:
        break;
    }
  }

  handleScroll(ev: Event) {
    const target = ev.target as HTMLElement;
    const PROGRESSIVE_LIST_BUFFER_PX = 250;
    const scrollBottom = target.scrollTop + target.offsetHeight;
    if (scrollBottom + PROGRESSIVE_LIST_BUFFER_PX >= target.scrollHeight) {
      this.increaseProgressiveListSize();
    }
    this.showScrolledTopShadow(target.scrollTop > 0);
  }

  handleResize(_ev) {
    this.update({});
  }

  showScrolledTopShadow(showScrollShadow) {
    if (this.showScrollShadow !== showScrollShadow) {
      // top shadow is a dynamic host style, therefore we need to use dom style api
      this.style.boxShadow = showScrollShadow ? `inset 0px 6px 10px -10px ${colors.gray2}` : `none`;
      this.showScrollShadow = showScrollShadow;
    }
  }

  getFocusedItemEl() {
    return this.el.querySelector<HTMLElement>(`.list-option.focused`);
  }

  getFocusedItemAnchorEl() {
    return this.el.querySelector<HTMLElement>(`.list-option.focused a`);
  }

  scrollToItemIfNeeded() {
    this.isScrollingIntoView = true;
    window.requestAnimationFrame(() => {
      const listEl = this.elRefs.containerEl;
      const focusedEl = this.getFocusedItemEl();
      if (!focusedEl || !this.elRefs.containerEl) {
        return;
      }

      // The top threshold should be large enough to account for section header
      const THRESHOLD = 45;
      const listElTop = listEl.scrollTop;
      const listElBottom = listElTop + listEl.offsetHeight;
      const focusedElTop = focusedEl.offsetTop - listEl.offsetTop;
      const focusedElBottom = focusedElTop + focusedEl.offsetHeight;
      if (focusedElTop - THRESHOLD < listElTop) {
        listEl.scrollTop = focusedElTop - THRESHOLD;
      } else if (focusedElBottom + THRESHOLD > listElBottom) {
        listEl.scrollTop = focusedElBottom + THRESHOLD - listEl.offsetHeight;
      }
      window.requestAnimationFrame(() => {
        this.isScrollingIntoView = false;
      });
    });
  }

  /** For mnemonic focus. Find item with label that matches prefix and focus on it */
  focusItemWithPrefix(prefix) {
    const {print} = this.getContext(`i18n`);
    const item = this.findItem((i) => print(i.wrapped.label).toUpperCase().startsWith(prefix.toUpperCase()));

    if (item) {
      this.focusItem(item.index);
    }
  }

  focusItemWithLabel(label) {
    const {print} = this.getContext(`i18n`);
    const item = this.findItem((i) => print(i.wrapped.label).toUpperCase() === label.toUpperCase());

    if (item) {
      this.focusItem(item.index);
    }
  }

  findSelectedItem(): WrappedSectionItem {
    return this.findItem((item) => item.wrapped.isSelected);
  }

  findItem(predicate: (item: WrappedSectionItem) => boolean): WrappedSectionItem {
    const sections = this.getDisplaySections();
    const items = findSelectedWrappedItems(sections, predicate);

    return items.length ? items[0] : null;
  }

  handleClickItem(item: WrappedSectionItem, ev: MouseEvent) {
    const modifierKeys = getModifierKeys(ev);
    const {selectionState, sections} = this.state;
    const eventDetail = {
      selectionState,
      modifierKeys,
    };

    if (item.wrapped.isSelectAll) {
      this.dispatchCustomEvent(`selectAll`, {detail: eventDetail});
    } else if (item.wrapped.isSelectAllMatching) {
      const searchFilter = this.attr(`search-filter`);
      const matchingSections = this.getMatchedSections(searchFilter, wrapSections(sections));

      const trimmedMatchingSections = unwrapSections(matchingSections);
      this.dispatchCustomEvent(`selectAllMatching`, {
        detail: {matchingSections: trimmedMatchingSections},
      });
    } else if (item.wrapped.isExpander) {
      this.handleShowAllItemClick(item);
    } else if (item.wrapped.expansionToggle) {
      this.handleExpansionToggleItemClick(item);
    } else if (!item.wrapped.isDisabled && !itemHasSubSections(item.wrapped)) {
      let shouldClose = true;

      if (item.wrapped.onSelect) {
        try {
          const result = item.wrapped.onSelect(item.wrapped);
          if (typeof result === `boolean`) {
            shouldClose = result;
          }
        } catch (_error) {
          shouldClose = true;
        }
      }

      if (item.wrapped.href) {
        this.getContext(`navManager`).navigate(item.wrapped.href, {
          triggerEvent: ev,
          fromAnchorLink: true,
          anchorTarget: item.wrapped.target,
        });
      }
      // DOM `click` event is replaced by `clickItem` custom event this component emits
      ev.stopPropagation();

      const itemClickEvent: ItemClickEvent = this.getClickItemEvent({
        item,
        modifierKeys,
        shouldClose,
      });

      this.dispatchEvent(itemClickEvent);
    }
  }

  handleSubItemsClickItem(ev: CustomEvent) {
    const {wrappedItem} = this.state.focusedItem;
    const parentItem = omit(wrappedItem.wrapped, `subSections`);
    const detail = {
      ...ev.detail,
      parentItems: [parentItem, ...(ev.detail.parentItems || [])],
    };

    this.dispatchCustomEvent(ev.type, {detail});
  }

  handleClickPill(item, modifierKeys) {
    const detail = {item: item.wrapped, modifierKeys};
    this.dispatchCustomEvent(`clickPill`, {detail});
  }

  handleShowAllItemClick(item: WrappedSectionItem) {
    const sectionIndex = item.sectionIndex;
    const fullyShownSections = this.state.fullyShownSections;
    fullyShownSections[sectionIndex] = true;
    this.update(fullyShownSections);

    const section = this.state.sections[item.sectionIndex];
    const clickExpanderItemEvent: ExpanderItemClickEvent = new CustomEvent(`clickExpanderItem`, {
      detail: {section, sectionIdx: sectionIndex},
    });
    this.dispatchEvent(clickExpanderItemEvent);
  }

  handleExpansionToggleItemClick(item: WrappedSectionItem): void {
    const {sectionIndex} = item;

    const {fullyShownSections} = this.state;
    fullyShownSections[sectionIndex] = !fullyShownSections[sectionIndex];
    this.update(fullyShownSections);

    const section = this.state.sections[item.sectionIndex];
    const clickEvent: ExpansionToggleItemClickEvent = new CustomEvent(`clickExpansionToggleItem`, {
      detail: {section, sectionIdx: sectionIndex, isExpanded: fullyShownSections[sectionIndex]},
    });
    this.dispatchEvent(clickEvent);
  }

  focusItem(itemIndex: number, {scrollIntoView = true, canShowSubItems = false, isAutoFocus = false} = {}) {
    const {wrappedItem, sectionDetails} = this.getSectionAndItemByIndex(itemIndex);
    const sectionItem = wrappedItem?.wrapped;
    if (sectionItem) {
      // Dispatch event if location or data has changed
      const itemHasChanged = !isEqual({wrappedItem, sectionDetails}, this.state.focusedItem);
      const focusHasChanged = this.state.focusedItemIndex !== wrappedItem.index;
      const hasNewFocusItem = itemHasChanged || focusHasChanged;
      this.update({
        focusedItemIndex: itemIndex,
        focusedItem: {wrappedItem, sectionDetails},
      });

      if (!isAutoFocus) {
        // Autofocus does not need to mess with sub menus.
        // Also, this fixes a bug where if the first element has sub menus, the sub menu doesn't expand when item hovered
        if (canShowSubItems && itemHasSubSections(sectionItem)) {
          this.openSubMenu(wrappedItem.index);
        } else {
          this.closeSubMenu();
        }
      }

      if (hasNewFocusItem) {
        this.dispatchEvent(
          new CustomEvent(`focusItem`, {detail: {item: sectionItem, section: sectionDetails}}) as ItemFocusEvent,
        );
        this.showContextPanelIfNeeded();
      }

      if (scrollIntoView) {
        this.scrollToItemIfNeeded();
      }
    } else {
      this.update({
        focusedItemIndex: -1,
        focusedItem: {sectionDetails: null, wrappedItem: null},
        tooltipData: null,
      });
    }
  }

  showContextPanelIfNeeded() {
    const newState = {
      tooltipData: null,
    };
    if (this.state.focusedItem && this.contextPanelTransform) {
      const {wrappedItem, sectionDetails} = this.state.focusedItem;
      const sectionItem = wrappedItem?.wrapped;
      if (sectionItem && !sectionItem.isDisabled && !itemHasSubSections(sectionItem)) {
        newState.tooltipData = this.contextPanelTransform(sectionItem, sectionDetails);
      }
    }
    this.update(newState);
  }

  getSectionAndItemByIndex(index: number): {sectionDetails: WrappedSectionDetails; wrappedItem: WrappedSectionItem} {
    for (const section of this.getFilteredSections()) {
      const wrappedItem = section.items.find((item) => item.index === index);
      if (wrappedItem) {
        return {wrappedItem, sectionDetails: omit(section, `items`, `sectionIndex`)};
      }
    }
    return {sectionDetails: null, wrappedItem: null};
  }

  getClickItemEvent({
    item,
    modifierKeys,
    shouldClose = true,
  }: {
    item: WrappedSectionItem;
    modifierKeys: ModifierKeys;
    shouldClose?: boolean;
  }): ItemClickEvent {
    const {sectionDetails} = this.state.focusedItem;
    const {selectionState} = this.state;

    return new CustomEvent(`clickItem`, {
      detail: {
        ...item.wrapped,
        shouldClose,
        selectionState,
        modifierKeys,
        section: sectionDetails,
        searchFilter: this.attr(`search-filter`),
        item: item.wrapped,
        topScrollPosition: this.elRefs.containerEl?.scrollTop,
      },
    });
  }

  getSelectionState(sections: Section[]) {
    const totalCount = countTotalItems(sections);
    const selectedCount = sections.reduce(
      (count, section) => count + section.items.filter((item) => item.isSelected).length,
      0,
    );

    switch (selectedCount) {
      case 0:
        return SelectionState.None;
      case totalCount:
        return SelectionState.All;
      default:
        return SelectionState.Some;
    }
  }

  getDisplaySections(): WrappedSection[] {
    let sections = this.getFilteredSections();
    sections = this.getProgressiveCappedSections(sections);
    sections = this.getFilledSections(sections);
    return sections;
  }

  /**
   * TODO(pablo): for completeness, nested sections should come up in the search results
   */
  getFilteredSections(): WrappedSection[] {
    const searchFilter = this.attr(`search-filter`);
    // Pass sections through a bunch of pure transforms and filter functions
    let sections = wrapSections(this.state.sections);
    if (searchFilter) {
      sections = this.getMatchedSections(searchFilter, sections);
      sections = this.appendHiddenSectionExpander(sections);
      sections = this.appendSectionFromInput(searchFilter, sections);
      sections = this.appendBulkSelectSection(searchFilter, sections);
    } else {
      // hidden sections only show up in search
      sections = getVisibleSections(sections);
      sections = this.appendBulkSelectSection(searchFilter, sections);
      // show limited sections only apply when user isn't searching
      sections = this.getShowLimitCappedSections(sections);
    }
    sections = this.appendExpansionToggle(sections);
    addIndicesToWrappedItems(sections);
    return sections;
  }

  // Append section only if there is no exact match
  canAppendSectionFromInput(searchFilter: string, sections: WrappedSection[]) {
    for (const section of sections) {
      for (const item of section.items) {
        if (!item.wrapped.excludeFromInputMatch && item.wrapped.label === searchFilter) {
          return false; // exact match, item already exists, terminate early.
        }
      }
    }
    return true;
  }

  getSectionFromCSVSearch(searchFilter: string) {
    const tokens = searchFilter.split(`,`);
    if (tokens.length > MAX_CSV_MATCHES) {
      return [];
    }
    const items = tokens
      .map((token) => {
        const trimmed = token?.trim();
        const key = trimmed?.toLowerCase();
        if (!trimmed) {
          return null;
        }
        return this.state.keyedItems[key] || {label: trimmed, value: trimmed, isSelected: false};
      })
      .concat(this.state.keyedItems[searchFilter?.toLowerCase()])
      .filter(Boolean); // Filters out empty strings and any other falsey value

    return [
      ...wrapSections([
        {
          items,
        },
      ]),
    ];
  }

  appendSectionFromInput(searchFilter: string, sections: WrappedSection[]) {
    const sectionsFromInputFn = this.state.sectionsFromInputFn;
    if (this.canAppendSectionFromInput(searchFilter, sections)) {
      const unwrappedSections = unwrapSections(sections);
      const newSections = sectionsFromInputFn && sectionsFromInputFn(searchFilter, unwrappedSections);
      if (newSections) {
        return [...sections, ...wrapSections(newSections)];
      }
    }

    return sections;
  }

  getSelectedTheme(selectedTheme: SelectedTheme = this.attr(`selected-theme`)): SelectedTheme {
    if (selectedTheme === SelectedTheme.Default) {
      return this.attr(`multiple`) ? SelectedTheme.Checkbox : SelectedTheme.Select;
    }
    return selectedTheme;
  }

  // Adds a virtual section or item for a select all option when in multiple mode
  appendBulkSelectSection(searchFilter: string, sections: WrappedSection[]) {
    const {selectionState} = this.state;
    const totalCount = countTotalItems(sections);

    const addSelectAll = this.attr(`show-select-all`) && !searchFilter;
    const addSelectAllMatching = this.attr(`show-select-all-matching`) && searchFilter;

    if ((addSelectAll || addSelectAllMatching) && totalCount > 0) {
      sections = [...sections];
      const isDisabled = this.attr(`select-all-disabled`);
      const selectAllItem =
        this.attr(`select-all-mechanism`) === SelectAllMechanism.Full ? SELECT_ALL_ITEM : DESELECT_ALL_ITEM;

      const sectionIndex = sections.length === 1 ? sections[0].sectionIndex : -1;
      const bulkSelectItem = addSelectAll
        ? wrapSectionItem(
            {
              ...selectAllItem,
              isDisabled,
              isSelected: selectionState === SelectionState.All,
              tooltip: this.attr(`select-all-tooltip`),
            },
            {sectionIndex},
          )
        : wrapSectionItem(
            {
              ...SELECT_ALL_MATCHING_ITEM,
              isDisabled: this.attr(`select-all-matching-disabled`),
              isSelected: this.allItemsSelected(sections),
            },
            {sectionIndex},
          );

      // if there is only a single section, then 'Select All' is added as the first
      // item, otherwise 'Select All' is added as the first section on its own.
      if (sections.length === 1) {
        sections[0] = {
          ...sections[0],
          items: [bulkSelectItem, ...sections[0].items],
        };
      } else {
        sections.unshift({items: [bulkSelectItem], sectionIndex});
      }
    }

    return sections;
  }

  appendHiddenSectionExpander(sections: WrappedSection[]) {
    return sections.map((section) => {
      const {sectionIndex} = section;
      if (section.isHidden && !this.state.fullyShownSections[sectionIndex] && section.items.length > 0) {
        return {
          ...section,
          items: [
            wrapSectionItem(
              {
                ...SHOW_HIDDEN_SECTION_ITEM,
                label: ({t, print}) =>
                  `${t(`components.items-menu.show-items`, {items: print(section.label)})} (${section.items.length})`,
                tooltip: section.tooltip,
              },
              {sectionIndex},
            ),
          ],
        };
      }

      return section;
    });
  }

  appendExpansionToggle(sections: WrappedSection[]): WrappedSection[] {
    return sections.map((section) => {
      const {expansionToggle} = section;
      if (!expansionToggle || (!expansionToggle.alwaysShow && !section.items.length)) {
        return section;
      }

      let label: Translatable;
      const isExpanded = Boolean(this.state.fullyShownSections[section.sectionIndex]);
      if (expansionToggle.labelMode === ExpansionToggleLabelMode.Unchanged) {
        label = section.label;
      } else {
        const expansionLabel = isExpanded
          ? ({t, print}) => t(`components.items-menu.hide-items`, {items: print(section.label)})
          : ({t, print}) => t(`components.items-menu.show-items`, {items: print(section.label)});
        label = ({print}) => `${print(expansionLabel)} (${section.items.length})`;
      }

      return {
        ...section,
        items: [
          wrapSectionItem(
            {
              ...EXPANSION_TOGGLE_ITEM,
              expansionToggle,
              icon: section.icon,
              label,
            },
            {sectionIndex: section.sectionIndex},
          ),
          ...(isExpanded ? section.items : []),
        ],
      };
    });
  }

  allItemsSelected(sections: WrappedSection[]): boolean {
    return sections.every((section) => section.items.every((item) => item.wrapped.isSelected));
  }

  getMatchedSections(searchFilter: string, sections: WrappedSection[]): WrappedSection[] {
    if (this.attr(`show-csv-matches`) && searchFilter.includes(`,`)) {
      return this.getSectionFromCSVSearch(searchFilter);
    }

    if (!this.isConnected) {
      return [];
    }

    const {print} = this.getContext(`i18n`);
    return sections.map((section) => ({
      ...section,
      items:
        this.attr(`show-section-label-matches`) && fuzzyMatch(print(section.label), searchFilter)
          ? section.items
          : this.getMatchingItems(searchFilter, section.items),
    }));
  }

  getShowLimitCappedSections(sections: WrappedSection[]) {
    const fullyShownSections = this.state.fullyShownSections;
    return sections.map((section) => {
      const showLimit = section.showLimit;
      let items = section.items;
      const {sectionIndex} = section;
      // Add a `show all (x)` item at the end of section and limit number of items displayed
      if (showLimit > 0 && items.length > showLimit && !fullyShownSections[sectionIndex]) {
        const showAllItem = {...SHOW_ALL_ITEM, sectionIndex, label: `${SHOW_ALL_ITEM.label} (${items.length})`};
        items = [...items.slice(0, showLimit), wrapSectionItem(showAllItem, {sectionIndex})];
      }
      return {...section, items};
    });
  }

  getProgressiveCappedSections(sections: WrappedSection[]) {
    const progressiveListSize = this.state.progressiveListSize;
    return sections.map((section) => ({
      ...section,
      items: section.items.filter((item) => item.index < progressiveListSize),
    }));
  }

  getFilledSections(sections: WrappedSection[], {excludeAlwaysShowLabel}: {excludeAlwaysShowLabel?: boolean} = {}) {
    const searchFilter = this.attr(`search-filter`);
    // Loading section only shown in non search scenarios
    return sections.filter((section) => {
      return (
        (!excludeAlwaysShowLabel && section.alwaysShowLabel) ||
        section.items.length > 0 ||
        ((!searchFilter || section.loadOnSearch) && section.isLoading)
      );
    });
  }

  getMatchingItems(searchFilter: string, items: WrappedSectionItem[]): WrappedSectionItem[] {
    const res = this.getHighlightedItems(searchFilter, items).filter(
      (item) => !!item.labelMatch || !!item.subLabelMatch || !!item.customMatch,
    );

    if (!this.attr(`disable-search-sort`)) {
      res.sort(
        lexicalCompose(
          (a, b) => b.isExactLabelMatch - a.isExactLabelMatch,
          sortOnProperty(`labelMatch`),
          sortOnProperty(`subLabelMatch`),
          sortOnProperty(`customMatch`),
        ),
      );
    }

    return res;
  }

  getHighlightedItems(searchFilter: string, items: WrappedSectionItem[]): WrappedSectionItem[] {
    if (this.attr(`search-exact`)) {
      searchFilter = `"` + searchFilter + `"`;
    }

    if (!this.isConnected) {
      return [];
    }

    const {print} = this.getContext(`i18n`);
    const searchFilterNormalized = searchFilter.toUpperCase();

    return items.map((item) => {
      const label = print(item.wrapped.label);

      return {
        ...item,
        isExactLabelMatch: label?.toUpperCase() === searchFilterNormalized,
        labelMatch: fuzzyMatch(label, searchFilter),
        subLabelMatch: fuzzyMatch(print(item.wrapped.subLabel), searchFilter),
        customMatch: this.state.customSearchMatcherFn?.(item.wrapped, searchFilter),
      };
    });
  }

  increaseProgressiveListSize() {
    let progressiveListSize = this.state.progressiveListSize;
    if (progressiveListSize < countTotalItems(this.getFilteredSections())) {
      progressiveListSize += PROGRESSIVE_SIZE;
      this.update({progressiveListSize});
    }
  }
}

export const ItemsMenuEl = defineAndMakeJsxFactory<ItemsMenuJsxProps>(`mp-items-menu`, ItemsMenu);
export const ItemsMenuReact = panel2react<ItemsMenuJsxProps>(
  `mp-items-menu`,
  ItemsMenu,
  // "sections" is both an attr and a prop, but we want to pass it through as a prop, which allows for passing in
  // functions (e.g. a custom renderer)
  {forceProperty: [`sections`]},
);
